import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarClock, Plus, Trash2, Send, Save, Loader2, BellRing } from "lucide-react";
import { toast } from "sonner";

const TEMPLATES = [
  { key: "rent_due", label: "Rent Due", tone: "text-emerald-700 bg-emerald-50" },
  { key: "rent_late", label: "Rent Late", tone: "text-amber-700 bg-amber-50" },
  { key: "pay_or_quit", label: "Pay or Quit", tone: "text-rose-700 bg-rose-50" },
];

export const ordinal = (n) => {
  const s = ["th", "st", "nd", "rd"], v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

export function ScheduleSettings() {
  const [rem, setRem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sendingKey, setSendingKey] = useState("");

  useEffect(() => { api.get("/settings/reminders").then((r) => setRem(r.data)); }, []);

  const persist = async (next, msg) => {
    setSaving(true);
    try {
      const { data } = await api.put("/settings/reminders", { auto_reminders_enabled: next.auto_reminders_enabled, schedules: next.schedules, nudge_days: Number(next.nudge_days ?? 3) });
      setRem(data);
      toast.success(msg || "Schedule saved");
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  const toggleMaster = (val) => persist({ ...rem, auto_reminders_enabled: val }, val ? "Scheduled notices on" : "Scheduled notices paused");
  const update = (i, patch) => setRem((r) => ({ ...r, schedules: r.schedules.map((s, idx) => (idx === i ? { ...s, ...patch } : s)) }));
  const remove = (i) => setRem((r) => ({ ...r, schedules: r.schedules.filter((_, idx) => idx !== i) }));
  const add = () => setRem((r) => ({ ...r, schedules: [...r.schedules, { id: "", template_type: "rent_late", day: 10, enabled: true }] }));

  const sendNow = async (s, i) => {
    setSendingKey(`${i}`);
    try {
      const { data } = await api.post("/reminders/run-now", { template_type: s.template_type });
      toast.success(data.sent ? `Sent to ${data.sent} tenant(s) with a balance` : "No tenants currently owe a balance — nothing sent");
    } catch (e) { toast.error(apiErr(e)); } finally { setSendingKey(""); }
  };

  const sendNudges = async () => {
    setSendingKey("nudge");
    try {
      const { data } = await api.post("/reminders/nudges/run-now");
      toast.success(data.sent ? `Rent-due nudge sent to ${data.sent} tenant(s)` : "No tenant is inside the nudge window right now (or already nudged today) — nothing sent");
    } catch (e) { toast.error(apiErr(e)); } finally { setSendingKey(""); }
  };

  if (!rem) return null;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="schedule-settings">
      <div className="flex items-start justify-between gap-3 mb-1">
        <div>
          <h2 className="font-display font-bold text-lg text-slate-900">Scheduled notices</h2>
          <p className="text-sm text-slate-500">Pick the day of the month each notice goes out automatically. Only tenants with an unpaid balance receive it.</p>
        </div>
        <Switch checked={rem.auto_reminders_enabled} onCheckedChange={toggleMaster} data-testid="settings-reminders-toggle" />
      </div>

      <div className={`mt-5 space-y-3 ${rem.auto_reminders_enabled ? "" : "opacity-50"}`}>
        {rem.schedules.map((s, i) => {
          const t = TEMPLATES.find((x) => x.key === s.template_type) || TEMPLATES[0];
          return (
            <div key={s.id || i} className="rounded-xl border border-slate-200 p-3.5" data-testid={`schedule-row-${i}`}>
              <div className="flex flex-wrap items-center gap-3">
                <span className={`h-9 w-9 rounded-lg flex items-center justify-center ${t.tone}`}><CalendarClock className="h-4 w-4" /></span>
                <div className="flex-1 min-w-[150px]">
                  <Select value={s.template_type} onValueChange={(v) => update(i, { template_type: v })}>
                    <SelectTrigger className="h-9" data-testid={`schedule-template-${i}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{TEMPLATES.map((x) => <SelectItem key={x.key} value={x.key}>{x.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  on the
                  <Input type="number" min={1} max={28} value={s.day} data-testid={`schedule-day-${i}`}
                    onChange={(e) => update(i, { day: Math.min(28, Math.max(1, Number(e.target.value) || 1)) })} className="h-9 w-16 text-center" />
                  <span className="text-xs text-slate-400 w-7">{ordinal(Number(s.day) || 1)}</span>
                </div>
                <Switch checked={s.enabled} onCheckedChange={(v) => update(i, { enabled: v })} data-testid={`schedule-enabled-${i}`} />
                <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-rose-600" onClick={() => remove(i)} data-testid={`schedule-remove-${i}`}><Trash2 className="h-4 w-4" /></Button>
              </div>
              <div className="flex items-center justify-between mt-2.5 pl-12">
                <span className="text-xs text-slate-500">{t.label} email · every month on the {ordinal(Number(s.day) || 1)}{s.enabled ? "" : " · paused"}</span>
                <button onClick={() => sendNow(s, i)} disabled={sendingKey !== ""} data-testid={`schedule-send-now-${i}`}
                  className="text-xs font-semibold text-primary flex items-center gap-1 hover:underline disabled:opacity-50">
                  {sendingKey === `${i}` ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />} Send now
                </button>
              </div>
            </div>
          );
        })}
        {rem.schedules.length === 0 && <p className="text-sm text-slate-500 text-center py-4">No schedules yet — add one below.</p>}
      </div>

      <div className="mt-5 rounded-xl border border-emerald-100 bg-emerald-50/60 p-3.5" data-testid="nudge-settings">
        <div className="flex flex-wrap items-center gap-2 text-sm text-slate-700">
          <BellRing className="h-4 w-4 text-emerald-700" />
          <span className="font-semibold text-slate-800">Rent-day banner:</span> show tenants a "pay now" banner in their portal
          <Input type="number" min={0} max={14} value={rem.nudge_days ?? 3} data-testid="nudge-days"
            onChange={(e) => setRem((r) => ({ ...r, nudge_days: Math.min(14, Math.max(0, Number(e.target.value) || 0)) }))} className="h-9 w-16 text-center" />
          day(s) before rent is due.
        </div>
        <p className="text-xs text-slate-500 mt-1.5 pl-6">It stays up until the copay is paid and turns red once rent is overdue. The same reminder is emailed (and texted to tenants who opted in) on that day and again on the due day — edit the wording under Notices → "Rent Due Soon". Saved with the schedule below.</p>
        <button onClick={sendNudges} disabled={sendingKey !== ""} data-testid="nudge-send-now" className="mt-2 ml-6 text-xs font-semibold text-primary flex items-center gap-1 hover:underline disabled:opacity-50">
          {sendingKey === "nudge" ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />} Send today's nudges now
        </button>
      </div>

      <div className="flex gap-2 mt-4">
        <Button variant="outline" className="gap-2" onClick={add} data-testid="schedule-add"><Plus className="h-4 w-4" /> Add schedule</Button>
        <Button className="gap-2 flex-1" onClick={() => persist(rem)} disabled={saving} data-testid="schedule-save"><Save className="h-4 w-4" /> {saving ? "Saving…" : "Save schedule"}</Button>
      </div>
    </div>
  );
}
